import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => setMounted(true));

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      className={clsx(
        'fixed top-0 right-0 left-0 z-50 transition-all duration-500',
        scrolled
          ? 'bg-alpine-950/80 border-b border-white/[0.05] backdrop-blur-xl'
          : 'bg-transparent',
        mounted ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      )}
      style={{ transitionDelay: mounted ? '0ms' : '100ms' }}
    >
      <nav className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="group flex items-baseline gap-0">
            <span className="font-display text-earth-cream text-lg tracking-wide transition-colors">
              CORE<span className="text-stone-500">ALPINE</span>
            </span>
            <span className="font-display text-forest-light group-hover:text-stream ml-0.5 text-sm transition-colors duration-300">
              .dev
            </span>
          </Link>

          {/* Navigation links */}
          <div className="hidden items-center gap-8 md:flex">
            <NavLink href="#about">About</NavLink>
          </div>

          {/* Sister site link - desktop */}
          <a
            href="https://corealpine.design"
            target="_blank"
            rel="noopener noreferrer"
            className="group hover:border-earth-sand/30 hidden items-center gap-2 rounded-full border border-stone-800 px-4 py-2 transition-all duration-300 md:flex"
          >
            <span className="font-body text-sm text-stone-500 transition-colors group-hover:text-stone-300">
              Visit
            </span>
            <span className="text-earth-sand font-display text-sm tracking-wide">.design</span>
            <svg
              className="group-hover:text-earth-sand h-3.5 w-3.5 text-stone-600 transition-all duration-300 group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>

          {/* Sister site link - mobile */}
          <a
            href="https://corealpine.design"
            target="_blank"
            rel="noopener noreferrer"
            className="text-earth-sand font-display text-sm md:hidden"
          >
            .design
          </a>
        </div>
      </nav>
    </header>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="font-body hover:text-earth-cream group relative text-sm tracking-widest text-stone-400 uppercase transition-colors duration-300"
    >
      {children}
      <span className="from-earth-sand via-forest to-stream absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 transform bg-gradient-to-r transition-transform duration-300 group-hover:scale-x-100" />
    </a>
  );
}
